import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  resolveBulkProductGroup,
  type BulkGroupProduct,
  type BulkProductGroup,
} from "../bulkProductGroup";
import { BULK_PRODUCTS_QUERY_PARAM, splitBulkProductIds } from "../paths";

/**
 * Owns the `?bulk=` query param: reads the group out of it, and rewrites it
 * whenever the resolved group no longer matches what it requested (a product of
 * the group was deleted, an id was duplicated...).
 *
 * Only the component driving the configuration may call this hook, since two
 * concurrent callers would fight over the param. Read-only consumers resolve
 * their group with `resolveBulkProductGroup` instead.
 */
export function useBulkProductGroup<TProduct extends BulkGroupProduct>({
  leaderProduct,
  products,
}: {
  leaderProduct: TProduct | undefined;
  products: ReadonlyArray<TProduct | null | undefined>;
}): BulkProductGroup<TProduct> {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawBulkParam = searchParams.get(BULK_PRODUCTS_QUERY_PARAM);

  const group = useMemo(
    () =>
      resolveBulkProductGroup({
        leaderProduct,
        products,
        requestedProductIds: splitBulkProductIds(rawBulkParam),
      }),
    [leaderProduct, products, rawBulkParam]
  );

  const sanitizedParam = group.bulkProductFollowerIds.join(",");
  const isGroupResolved = Boolean(leaderProduct) && products.length > 0;

  useEffect(() => {
    if (
      !isGroupResolved ||
      rawBulkParam === null ||
      rawBulkParam === sanitizedParam
    ) {
      return;
    }

    setSearchParams(
      currentParams => {
        const nextParams = new URLSearchParams(currentParams);

        if (sanitizedParam) {
          nextParams.set(BULK_PRODUCTS_QUERY_PARAM, sanitizedParam);
        } else {
          nextParams.delete(BULK_PRODUCTS_QUERY_PARAM);
        }

        return nextParams;
      },
      { replace: true }
    );
  }, [isGroupResolved, rawBulkParam, sanitizedParam, setSearchParams]);

  return group;
}
